// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGridHorizontal.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartGridVerticalHorizontal component
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect';
import { Table, Column, ColumnGroup } from 'share/components/controls/DataGrid/Izenda.Table';

import reportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import classnames from 'classnames';
import { GRID_STYLES } from 'logic/data/Izenda.ReportPartGridConstants';
import { CUSTOM_FORMAT_PREFIX } from 'logic/data/Izenda.ReportPartChartConstants';
import SubReportLink from './Izenda.SubReportLink';
import PrintingUtils from 'share/utils/Izenda.PrintingUtils';

import actions from './Izenda.ReportPartGridVerticalHorizontalActions';
import ObjectUtils from 'share/utils/Izenda.ObjectUtils';
import AppGlobal from 'Izenda.AppGlobal';
import { isNil, get, isObject, getOr, any, reduce, map, isEmpty, find } from 'lodash/fp';
import reportPartElementUtils from "share/utils/Izenda.ReportPartElementUtils";

const UNDEFINED_VALUES = ['null', ''];

@connect(actions)
export default class ReportPartGridVerticalHorizontal extends Component {
  static propTypes = {
    reportPart: PropTypes.object.isRequired,
    records: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired
  };

  constructor() {
    super();
    this.renderListeners = []; // for cells
  }

  componentDidMount() {
    const props = this.props;
    props.printingMode &&
      PrintingUtils.publishFinishedRender(this.renderListeners, () => {
        props.finishedRender && props.finishedRender();
      });
  }

  /**
   * render table
   */
  render() {
    const result = this.props.generateHeaderData(this.props);
    return <section>{result.map(this.renderTable)}</section>;
  }

  /**
   * render table
   */
  renderTable = (page: any, index: number) => {
    const props = this.props;
    const { staticRender, afterRenderCallBack, reportPart, getStickyValue } = props;
    const { headers, rowSpan } = page;
    const gridStyle = reportPart.reportPartContent.gridStyle;
    const properties = reportPart.getReportPartProperties();

    const className = classnames(
      'report-part-grid-preview',
      { hide: isNil(headers) },
      { 'vertical-grid': gridStyle === GRID_STYLES.VERTICAL.value },
      { 'horizontal-grid': gridStyle === GRID_STYLES.HORIZONTAL.value }
    );

    if (rowSpan) {
      rowSpan.cell = get('title');
      rowSpan.fnGetCellStyle = props.getColumnGroupCellStyle(rowSpan.data.length);
      rowSpan.fnGetCellContentStyle = props.getColumnGroupCellContentStyle;
    }

    return (
      <section key={index} className={className}>
        {index > 0 && <div className="u_alwaysBreakAfter" />}
        <Table
          showScroll={false}
          dataSource={page.records}
          className="custom-table"
          autoResize={false}
          afterRenderCallBack={afterRenderCallBack}
          rowSpan={rowSpan}
          staticRender={staticRender}
          onResizeStopCallback={this.onResizeStopCallback}
          stickVertical={getStickyValue(properties.isHeaderStickVertical)}
          stickHorizontal={getStickyValue(properties.isHeaderStickHorizontal)}
        >
          {this.renderColumns(page.headers, index, page.records.length)}
        </Table>
      </section>
    );
  };

  /**
   * on resize Stop Callback
   * @param  {[type]} resizeInfo
   */
  onResizeStopCallback = resizeInfo => {
    const { index, columnWidth } = resizeInfo;
    const columnCollection = get('reportPart.reportPartContent.columns.elements', this.props);

    if (!isEmpty(columnCollection)) {
      const field = find({ position: index + 1 }, columnCollection);
      field && field.updateColumnWidth(columnWidth);
    }
  };

  /**
   * render cell content base on value and style
   * @method renderColumnContent
   * @param  {[type]}            dataRow        [record for this dataRow]
   * @return {React.element}                    [cell content]
   */
  renderColumnContent = (
    header: any,
    pageIndex: number,
    dataRow: any,
    columnKey: string,
    rowIndex: number,
    column: any,
    columnIndex: number,
    afterRenderCallBack: any
  ) => {
    // get cell data from vertical/horizontal table
    const props = this.props;
    const { printingMode, reportPart } = props;
    const properties = reportPart.getReportPartProperties();
    const rawRecords = props.records.slice();
    const isHorizontal = properties.generalInfo.gridStyle === GRID_STYLES.HORIZONTAL.value ? 1 : 0;
    if (isHorizontal && !dataRow[columnIndex]) {
      return null;
    }
    const pivotColumnsPerExportedPage = properties.view.pivotColumnsPerExportedPage || 0;
    const columnHeader = isHorizontal ? dataRow : header;
    const fieldElement = get('element', columnHeader);
    let cell, originalCell;
    const isSubTotal = isHorizontal ? dataRow[columnIndex].className === 'sub-total' : get('isSubTotal', dataRow);
    const originalColumnKey = !isNil(header.columnKey) ? CUSTOM_FORMAT_PREFIX + header.columnKey : null;

    if (isSubTotal) {
      cell = isHorizontal ? dataRow[columnIndex] : dataRow[fieldElement.fieldNameAlias];
    } else {
      cell = !isNil(dataRow[header.columnKey]) ? dataRow[header.columnKey] : dataRow[columnIndex];
      originalCell = !isNil(originalColumnKey) ? dataRow[originalColumnKey] : dataRow[columnIndex];
    }

    const basedData = isHorizontal || isObject(cell) ? cell && cell.data : cell;
    const originalData = isHorizontal || isObject(originalCell) ? originalCell && originalCell.originalData : originalCell;
    const convertNullToEmpty = AppGlobal.advancedSettings.other.convertNullToEmptyStringValue;
    let cellData = String.commonStringValue(basedData, convertNullToEmpty);
    const fieldElementProperties = get('properties', fieldElement);
    const cellFormat = isSubTotal
      ? get('subTotalFormat.format', cell)
      : get('dataFormattings.format.format', fieldElementProperties);
    const subTotalDataType = get('subTotalDataType', cell);

    if (!isHorizontal || columnIndex > 0) {
      // get correct data row index in real data source. if horizontal it is column index + pagesize number
      const dataRowIndex = !isHorizontal ? rowIndex : pageIndex * pivotColumnsPerExportedPage + columnIndex - 1;
      // use this data row for filter in sub report/ custom URL
      const rawRecord = rawRecords[dataRowIndex];

      cellData = reportPartUtils.getDataFormat(
        {
          originalValue: originalData,
          value: cellData
        },
        {
          alternativeText: get('dataFormattings.alternativeText', fieldElementProperties),
          fieldFormatData: cellFormat,
          fieldDataType: isSubTotal ? subTotalDataType : get('previewDataType', fieldElement)
        },
        {
          element: reportPartUtils.buildDataSourceElement(reportPart, fieldElement),
          record: rawRecord
        }
      );

      const numberOfPointActions = get('numberOfPointActions', fieldElement);
      var label = getOr(cellData, 'title', cell);

      if (rawRecord && numberOfPointActions > 0) {
        const id = [pageIndex, columnIndex, rowIndex].join('.');
        PrintingUtils.pushRenderListener(this.renderListeners, id);
        return (
          <SubReportLink
            reportPart={reportPart}
            fieldElement={fieldElement}
            afterRenderCallBack={afterRenderCallBack}
            printingMode={printingMode}
            finishedRender={PrintingUtils.getFinishedRenderPromiseById(this.renderListeners, id)}
            getFilterInfoForSubReport={props.getFilterInfoForSubReport}
            rawRecord={[rawRecord]}
          >
            {cellData}
          </SubReportLink>
        );
      }
    }
    if (isHorizontal && columnIndex === 0) {
      return props.renderHeader(reportPart.reportPartContent.rows, fieldElement, cellData, afterRenderCallBack);
    }

    return ObjectUtils.isImage(columnHeader.element.field.dataFieldType) &&
      (!isHorizontal || (isHorizontal && columnIndex > 0)) &&
      !any(value => cellData === value, UNDEFINED_VALUES) ? (
      <img src={cellData} />
    ) : (
      <span key={cellData} title={label} className={(cell && cell.className) || dataRow.className}>
        {cellData}
      </span>
    );
  };

  /**
   * Render column
   */
  renderColumn = (totalColumns: any, numberOfRecord: number, pageIndex: number, header: any, index: number) => {
    const props = this.props;
    const properties = props.reportPart.getReportPartProperties();
    const {
      isHeaderStickHorizontal,
      generalInfo: { gridStyle }
    } = properties;
    const isHorizontal = gridStyle === GRID_STYLES.HORIZONTAL.value;
    const isStickyLeft = props.getStickyValue(index === 0 && isHorizontal && isHeaderStickHorizontal);

    return (
      <Column
        className={isStickyLeft ? 'u-stickLeft' : ''}
        key={header.columnKey}
        {...header}
        cell={this.renderColumnContent.bind(this, header, pageIndex)}
        fnGetCellContentStyle={props.fnGetCellContentStyle(header)}
        fnGetHeaderCellStyle={props.getCellStyle(header, numberOfRecord, totalColumns)}
        fnGetHeaderCellContentStyle={props.getHeaderCellStyle(header)}
        fnGetCellStyle={props.getCellStyle(header, numberOfRecord, totalColumns)}
      />
    );
  };

  /**
   * Render columns
   */
  renderColumns = (headers: any, pageIndex: number, numberOfRecord: number) => {
    const props = this.props;
    const totalColumns = reduce(
      (acc, header) => {
        if (header.isColumnGroup) {
          return acc + header.child.length;
        }
        return acc + 1;
      },
      0,
      headers
    );

    return headers.map((header, index) => {
      if (!header.isColumnGroup) {
        return this.renderColumn(totalColumns, numberOfRecord, pageIndex, header, index);
      }
      return (
        <ColumnGroup
          key={index}
          title={header.title}
          fnGetCellStyle={props.getColumnGroupCellStyle(headers.length, header, index)}
          fnGetCellContentStyle={props.getColumnGroupCellContentStyle(header)}
        >
          {map(
            headerItem => this.renderColumn(totalColumns, numberOfRecord, pageIndex, headerItem, index),
            header.child
          )}
        </ColumnGroup>
      );
    });
  };
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGridVerticalHorizontal.js